import React, { Fragment } from 'react';
import Link from 'next/link';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { connectStateResults } from 'react-instantsearch-dom';

import { getOverrideMeta } from '../../utils/metaOverrides';
import PageHeader from '../../tissues/page-header';
import SLIHyphenMap from '../../utils/SLIHyphenMap.json';
import slugToSLI from '../../utils/slugToSLI';
import Typography from '../../atoms/typography';
import sliCategories from './utils/categories.json';
import refinementMap from './utils/refinement-map.json';
import { TRENDS_MAP } from './utils/helpers';

const SubHeader = styled(Typography)`
  color: ${(props) => props.theme.color.brandLight};
  letter-spacing: inherit;
  text-decoration: none;
`;

const SubHeaderLink = styled(SubHeader)`
  color: ${(props) => props.theme.color.brand};
  transition: color ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuad};

  &:hover {
    color: ${(props) => props.theme.color.brandLight};
  }
`;

const HeaderText = styled(Typography)`
  text-transform: capitalize;
`;

const HeaderLink = styled(HeaderText)`
  color: ${(props) => props.theme.color.brand};
  transition: color ${(props) => props.theme.animation.default} ${(props) => props.theme.animation.easeOutQuad};
  text-decoration: none;

  &:hover {
    color: ${(props) => props.theme.color.brandLight};
  }
`;

const ResultsHeader = ({
  brand,
  edit,
  lpc,
  trend,
  searchResults,
  searchState
}) => {
  const hasResults = searchResults && searchResults.nbHits !== 0;
  const query = searchState && searchState.query;
  const { hierarchicalMenu } = searchState;

  const getOverrideMetaProps = (type, payload) => {
    if (!searchState) return null;
    const cats = searchState.hierarchicalMenu?.['categories.lvl0']?.split(' > ') ?? [];

    const activeCategories = cats.reduce((acc, cat, idx) => {
      const category = sliCategories[idx].values.find((x) => x.name === cat);
      return { ...acc, [`cat${idx + 1}`]: { ...category, selected: true } };
    }, {});

    const categories = {
      defaults: sliCategories,
      active: activeCategories
    };

    const facets = searchState.refinementList && Object.entries(searchState.refinementList)
      .map(([key, values]) => {
        const facet = refinementMap[key];

        return {
          id: facet.id,
          name: facet.label,
          values: Array.isArray(values) ? values.map((x) => ({
            id: slugToSLI(x.toLowerCase()),
            name: x,
            selected: true
          })) : []
        };
      });

    return getOverrideMeta([], type, { ...payload, categories, facets });
  };

  if (edit || lpc || !hasResults) return null;

  if (brand) {
    const { heading } = getOverrideMetaProps('brand', { brand });

    return (
      <PageHeader
        heading={heading}
        subHeading="Brand"
        text={brand.description ?? ''}
        image={brand.icon ?? ''}
        brandFilters={brand.permalink === 'brands/maison-me' ? [
          ['Baby', { cats: ['baby'] }],
          ['Kids', { cats: ['kids'] }],
          ['Adult', { af: ['agerange:adults'] }],
          ['Shop All', {}]
        ] : null}
      />
    );
  }

  if (hierarchicalMenu && hierarchicalMenu['categories.lvl0']) {
    const category = hierarchicalMenu['categories.lvl0'];
    const categories = category.split(' > ').map((x) => {
      const slug = slugToSLI(x.toLowerCase());
      return {
        name: x,
        id: SLIHyphenMap[slug] ?? slug
      };
    });

    const SubHeading = () => {
      const cats = trend
        ? categories
        : categories.slice(0, categories.length - 1);

      const getHref = (idx) => {
        if (idx === 1) return `/${trend ? `trends/${trend}` : 'shop'}/${cats[0].id}/${cats[1].id}`;
        return `/${trend ? `trends/${trend}` : 'shop'}/${cats[0].id}`;
      };

      if (trend) {
        return cats.map((cat, idx) => (idx + 1 !== cats.length
          ? (
            <Fragment key={cat}>
              <Link href={getHref(idx)} passHref>
                <SubHeaderLink element="a" like="label-1">{cat.name}</SubHeaderLink>
              </Link>

              {cats.length >= 2 && idx < cats.length - 1 && ' | '}
            </Fragment>
          ) : (
            <Fragment key={cat}>
              <SubHeader element="span" like="label-1">{cat.name}</SubHeader>

              {cats.length >= 2 && idx < cats.length - 1 && ' | '}
            </Fragment>
          )));
      }

      return (
        cats.map((cat, idx) => (
          <Fragment key={cat}>
            <Link href={getHref(idx)} passHref>
              <SubHeaderLink element="a" like="label-1">{cat.name}</SubHeaderLink>
            </Link>

            {cats.length === 2 && idx === 0 && ' | '}
          </Fragment>
        ))
      );
    };

    const Heading = () => {
      if (TRENDS_MAP[trend]) {
        return (
          <Link href={`/trends/${trend}`} passHref>
            <HeaderLink element="a" like="heading-2">{ TRENDS_MAP[trend]}</HeaderLink>
          </Link>
        );
      }

      const cat = categories[categories.length - 1];
      return cat.name;
    };

    if (TRENDS_MAP[trend]) {
      return <PageHeader subHeading={<SubHeading />} heading={<Heading />} />;
    }

    if (categories.length === 1) return <PageHeader heading={<Heading />} />;

    return <PageHeader subHeading={<SubHeading />} heading={<Heading />} />;
  }

  if (TRENDS_MAP[trend]) {
    const { heading } = getOverrideMetaProps('trend', { trend });
    return <PageHeader heading={heading || TRENDS_MAP[trend]} />;
  }
  if (query) return <PageHeader subHeading="search" heading={query} />;

  return null;
};

ResultsHeader.defaultProps = {
  brand: null,
  edit: null,
  lpc: null,
  trend: null,
  searchResults: null,
  searchState: null
};

ResultsHeader.propTypes = {
  brand: PropTypes.object,
  edit: PropTypes.string,
  lpc: PropTypes.string,
  trend: PropTypes.string,
  searchResults: PropTypes.object,
  searchState: PropTypes.object
};

export default connectStateResults(ResultsHeader);
